import { type SyntheticEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { errorMessage } from '@/api/client';
import { useServices } from '@/app/services';
import { Button, TextField } from '@/ui/components';
import { Icon } from '@/ui/Icon';
import { useAuth } from './useAuth';

function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <main className="auth">
      <div className="auth__card">
        <div className="auth__logo" aria-hidden="true">
          <Icon name="tag" size={36} />
        </div>
        <h1 className="auth__title">{title}</h1>
        {subtitle && <p className="auth__subtitle">{subtitle}</p>}
        {children}
      </div>
    </main>
  );
}

/** Sign in with email and password. */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await login(email, password);
      const next = params.get('next');
      void navigate(next?.startsWith('/') && !next.startsWith('//') ? next : '/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Willkommen bei tagn" subtitle="Melde dich an, um deine Notizen zu synchronisieren.">
      <form className="auth__form" onSubmit={(e) => void submit(e)}>
        <TextField label="E-Mail" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField
          label="Passwort"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Anmelden …' : 'Anmelden'}
        </Button>
      </form>
      <p className="auth__hint">Die Registrierung ist nur mit Einladung möglich.</p>
    </AuthLayout>
  );
}

/** Registration via invitation link (/register?token=…). */
export function RegisterPage() {
  const { api } = useServices();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [email, setEmail] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string>();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.auth
      .getInvitation({ token })
      .then((res) => setEmail(res.invitation?.email ?? ''))
      .catch((err: unknown) => setInviteError(errorMessage(err)));
  }, [api, token]);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (password.length < 10) {
      setError('Das Passwort muss mindestens 10 Zeichen lang sein.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await register(token, name, password);
      void navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const invalid = token ? inviteError : 'Der Einladungslink ist unvollständig.';
  if (invalid) {
    return (
      <AuthLayout title="Einladung ungültig" subtitle={invalid}>
        <Link className="btn btn--tonal" to="/login">
          Zur Anmeldung
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Konto erstellen"
      subtitle={
        email ? (
          <>
            Du wurdest als <strong>{email}</strong> eingeladen.
          </>
        ) : (
          'Einladung wird geprüft …'
        )
      }
    >
      <form className="auth__form" onSubmit={(e) => void submit(e)}>
        <TextField label="Name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        <TextField
          label="Passwort"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          supporting="Mindestens 10 Zeichen"
          error={error}
        />
        <Button type="submit" disabled={busy || email === null}>
          {busy ? 'Wird erstellt …' : 'Registrieren'}
        </Button>
      </form>
    </AuthLayout>
  );
}
